import React from 'react';
import { ScheduleShift } from 'containers/jobsite/tabs/jobsiteSchedules/data/types';
import {
  Button,
  EllipsisDropdownButton,
  MenuItemProps,
} from '@odin-labs/components';
import { AuthContext } from 'auth/authContext';
import { to } from 'acl';
import { PenIcon, PlusIcon, XMarkLargeIcon } from 'components/icons';
import {
  EditScheduleWorkerModal,
  DeleteShiftModal,
  EditShiftModal,
} from 'containers/jobsite/tabs/jobsiteSchedules/modals';
import {
  ensureNonEmptyItems,
  formatTimeTo12HourFormat,
  useBoolean,
} from 'utils';
import { useJobsiteSchedules } from './jobsiteSchedulesProvider';

export function JobsiteScheduleShiftHeader({
  shift,
}: {
  shift: ScheduleShift;
}): React.ReactElement {
  // TODO: Temporary flag. Remove hideRemoveShift and its reference when we allow for multiple shifts.
  const hideRemoveShift = true;
  const { jobsiteId, selectedSchedule, isScheduleEditable, canChangeSchedule } =
    useJobsiteSchedules();
  const attendance = shift.attendance?.edges.map(({ node }) => node) ?? [];
  const workers = shift.workers?.edges.map(({ node }) => node) ?? [];

  const { currentUser: user } = React.useContext(AuthContext);

  const {
    value: isAddWorkersModalOpen,
    setTrue: openAddWorkersModal,
    setFalse: closeAddWorkersModal,
  } = useBoolean();

  const {
    value: isEditShiftModalOpen,
    setTrue: openEditShiftModal,
    setFalse: closeEditShiftModal,
  } = useBoolean();

  const {
    value: isDeleteShiftModalOpen,
    setTrue: openDeleteShiftModal,
    setFalse: closeDeleteShiftModal,
  } = useBoolean();

  const totalWorking = attendance.filter(({ session }) => session).length ?? 0;

  const totalScheduled = workers.reduce(
    (acc, sw) => acc + (sw?.quantity ?? 0),
    0,
  );

  const totalContractors = new Set(
    workers
      .map((sw) => sw.jobsiteContractor?.contractor?.contractorId)
      .filter(Boolean),
  ).size;

  const menuItems = ensureNonEmptyItems<MenuItemProps>([
    isScheduleEditable &&
      user.isAllowed(to.editScheduleShift, jobsiteId) && {
        icon: PenIcon,
        onClick: openEditShiftModal,
        text: 'Edit shift',
      },
    !hideRemoveShift &&
      canChangeSchedule &&
      user.isAllowed(to.deleteScheduleShift, jobsiteId) && {
        icon: XMarkLargeIcon,
        onClick: openDeleteShiftModal,
        text: 'Remove shift',
        theme: 'danger',
      },
  ]);

  return (
    <>
      <div className="odin-flex odin-gap-4">
        <div className="odin-flex odin-gap-4 odin-flex-1 odin-items-center">
          <dl className="odin-flex odin-gap-4">
            <div className="odin-flex odin-gap-1">
              <dt className="odin-font-bold">Start time:</dt>
              <dd>{formatTimeTo12HourFormat(shift.startTime)}</dd>
            </div>
            <div className="odin-flex odin-gap-1">
              <dt className="odin-font-bold">Duration:</dt>
              <dd>{shift.duration} hrs</dd>
            </div>
          </dl>
        </div>
        <div className="odin-flex odin-gap-4 odin-items-center">
          <p className="odin-font-bold">
            {totalWorking ? `${totalWorking} / ` : ''}
            {totalScheduled} workers, {totalContractors} organizations
          </p>
          {isScheduleEditable &&
            user.isAllowed(to.addScheduleWorker, jobsiteId) && (
              <Button
                icon={PlusIcon}
                size="sm"
                text="Add workers"
                theme="secondary"
                onClick={openAddWorkersModal}
              />
            )}
          {menuItems.length > 0 && (
            <EllipsisDropdownButton menuItems={menuItems} />
          )}
        </div>
      </div>
      <EditScheduleWorkerModal
        editType="create"
        isOpen={isAddWorkersModalOpen}
        closeModal={closeAddWorkersModal}
        selectedSchedule={selectedSchedule}
        shift={shift}
      />
      <EditShiftModal
        isOpen={isEditShiftModalOpen}
        closeModal={closeEditShiftModal}
        shift={shift}
      />
      <DeleteShiftModal
        isOpen={isDeleteShiftModalOpen}
        closeModal={closeDeleteShiftModal}
        shift={shift}
      />
    </>
  );
}
